import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Weight, AlertTriangle, Package } from "lucide-react";
import { api, API } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/utils-date";
import { formatKg } from "@/lib/format";

function parseWeightKg(v) {
  if (!v) return 0;
  const s = String(v).trim().toLowerCase().replace(",", ".");
  const m = s.match(/^\s*([0-9]*\.?[0-9]+)\s*([a-z]*)/);
  if (!m) return 0;
  const num = parseFloat(m[1]);
  const unit = (m[2] || "kg").trim();
  if (unit === "g" || unit === "gr") return num / 1000;
  if (unit === "hg") return num / 10;
  return num;
}

// FEFO: expiry_date ascending, nulls last; ties by received_at ascending.
function _lotSortFEFO(a, b) {
  const ax = a.expiry_date || null;
  const bx = b.expiry_date || null;
  if (ax && !bx) return -1;
  if (!ax && bx) return 1;
  if (ax && bx && ax !== bx) return ax < bx ? -1 : 1;
  const ar = a.received_at || "";
  const br = b.received_at || "";
  if (ar !== br) return ar < br ? -1 : 1;
  return 0;
}

function _lotAvailable(lot, isWeight) {
  return isWeight ? Number(lot.stock_kg || 0) : Number(lot.stock || 0);
}

function _formatLotAvailable(lot, isWeight) {
  const v = _lotAvailable(lot, isWeight);
  return isWeight ? formatKg(v) : `${v} pz`;
}

export default function SaleSheet({ open, onOpenChange, product, onSaved, products: inlineProducts }) {
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const searchSeqRef = useRef(0);
  const [mode, setMode] = useState("piece"); // "piece" | "weight"
  const [quantity, setQuantity] = useState(1);
  const [weightStr, setWeightStr] = useState("");
  const [unitPrice, setUnitPrice] = useState(0); // €/pz
  const [pricePerKg, setPricePerKg] = useState(0); // €/kg
  const [saving, setSaving] = useState(false);

  // === Multi-Lotto: selezione lotto esplicita ===
  const [selectedLotId, setSelectedLotId] = useState(null);
  // In caso di errore "lot_insufficient" il backend risponde con l'elenco lotti
  // aggiornato: lo mostriamo in un banner sopra al selettore per far scegliere
  // di nuovo (nessuna cascata automatica, decide l'operatore).
  const [lotInsufficient, setLotInsufficient] = useState(null); // { lot_id, lot_available, requested, unit, lots: [...] }

  useEffect(() => {
    if (!open) return;
    const term = searchTerm.trim();
    if (inlineProducts && !term) {
      setProducts(inlineProducts);
      return;
    }
    const mySeq = ++searchSeqRef.current;
    const t = setTimeout(() => {
      api.listProducts(term ? { search: term } : {}).then((data) => {
        if (mySeq !== searchSeqRef.current) return;
        setProducts(data || []);
      }).catch(() => {
        if (mySeq !== searchSeqRef.current) return;
        setProducts([]);
      });
    }, term ? 250 : 0);
    return () => clearTimeout(t);
  }, [open, inlineProducts, searchTerm]);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const applyProduct = (p) => {
    if (!p) return;
    const isWeight =
      p.sale_unit === "weight" ||
      (p.sale_unit == null && Number(p.price_per_kg) > 0);
    if (isWeight) {
      setMode("weight");
      setPricePerKg(Number(p.price_per_kg) || 0);
      setUnitPrice(Number(p.price) || 0);
      setWeightStr("");
    } else {
      setMode("piece");
      setUnitPrice(Number(p.price) || 0);
      setPricePerKg(Number(p.price_per_kg) || 0);
      setWeightStr("");
    }
    setQuantity(1);
    // Reset lot: verrà preselezionato dall'effetto sui lotti disponibili
    setSelectedLotId(null);
    setLotInsufficient(null);
  };

  const changeMode = (newMode) => {
    setMode(newMode);
    const p = selected;
    if (!p) return;
    if (newMode === "weight") {
      setPricePerKg(Number(p.price_per_kg) || Number(pricePerKg) || 0);
    } else {
      setUnitPrice(Number(p.price) || Number(unitPrice) || 0);
    }
  };

  useEffect(() => {
    if (product) {
      setProductId(product.id);
      applyProduct(product);
    } else if (open) {
      setProductId("");
      setQuantity(1);
      setUnitPrice(0);
      setPricePerKg(0);
      setWeightStr("");
      setMode("piece");
      setSelectedLotId(null);
      setLotInsufficient(null);
    }
  }, [product, open]);

  const handleProductChange = (id) => {
    setProductId(id);
    const p = products.find((x) => x.id === id);
    if (p) {
      setLastSelectedProduct(p);
      applyProduct(p);
    }
  };

  const [lastSelectedProduct, setLastSelectedProduct] = useState(null);
  useEffect(() => {
    if (!open) setLastSelectedProduct(null);
  }, [open]);
  useEffect(() => {
    if (product) setLastSelectedProduct(product);
  }, [product]);

  const visibleProducts = useMemo(() => {
    if (!lastSelectedProduct) return products;
    const has = products.some((p) => p.id === lastSelectedProduct.id);
    return has ? products : [lastSelectedProduct, ...products];
  }, [products, lastSelectedProduct]);

  const selected = product || visibleProducts.find((p) => p.id === productId);
  const availableKg = selected?.stock_kg;
  const isWeight = mode === "weight";

  // Lotti disponibili del prodotto selezionato: solo quelli con giacenza > 0,
  // ordinati FEFO (scadenza più vicina prima). Un solo lotto → nessun selettore.
  const availableLots = useMemo(() => {
    if (!selected?.lots || !Array.isArray(selected.lots)) return [];
    return selected.lots
      .filter((l) => _lotAvailable(l, isWeight) > 0)
      .slice()
      .sort(_lotSortFEFO);
  }, [selected, isWeight]);

  // Preselect FEFO quando cambia prodotto o modalità
  useEffect(() => {
    if (availableLots.length === 0) {
      setSelectedLotId(null);
      return;
    }
    // Se il lotto attualmente selezionato esiste ancora → mantienilo; altrimenti FEFO
    setSelectedLotId((prev) => {
      if (prev && availableLots.some((l) => l.id === prev)) return prev;
      return availableLots[0].id;
    });
  }, [availableLots]);

  // In caso di errore lot_insufficient, il banner mostra i lotti dal backend
  // (che riflettono lo stock aggiornato dopo eventuali vendite parallele).
  const insufficientLots = lotInsufficient?.lots || null;

  const total = useMemo(() => {
    if (mode === "weight") {
      const kg = parseWeightKg(weightStr);
      return kg * (Number(pricePerKg) || 0);
    }
    return (Number(unitPrice) || 0) * (Number(quantity) || 0);
  }, [mode, weightStr, pricePerKg, unitPrice, quantity]);

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) return;
    setSaving(true);
    setLotInsufficient(null);
    try {
      // Payload comune con lot_id esplicito (se ci sono lotti; altrimenti null e
      // il backend cade sul fallback FEFO — copre anche i prodotti legacy senza
      // lots inizializzati).
      const lotId = availableLots.length > 0 ? (selectedLotId || availableLots[0].id) : null;
      if (mode === "weight") {
        const kg = parseWeightKg(weightStr);
        if (!kg || !pricePerKg) {
          toast.error("Inserisci peso e prezzo al kg");
          setSaving(false);
          return;
        }
        await axios.post(`${API}/sales`, {
          product_id: productId,
          quantity: 1,
          unit_price: Number(pricePerKg),
          price_per_kg: Number(pricePerKg),
          weight_sold: weightStr,
          lot_id: lotId,
        });
      } else {
        if (quantity <= 0) {
          setSaving(false);
          return;
        }
        await api.createSale({
          product_id: productId,
          quantity: Number(quantity),
          unit_price: Number(unitPrice),
          lot_id: lotId,
        });
      }
      toast.success("Vendita registrata");
      onSaved?.();
    } catch (err) {
      // Guardia lotto insufficiente: il backend risponde con detail.code === "lot_insufficient"
      // e l'elenco aggiornato dei lotti. Non chiudiamo il sheet: mostriamo il banner
      // e lasciamo scegliere di nuovo.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "lot_insufficient") {
        setLotInsufficient(detail);
        // Deseleziona il lotto insufficiente così l'operatore fa una scelta esplicita
        setSelectedLotId(null);
        toast.warning("Lotto insufficiente", {
          description: `${detail.lot_available} ${detail.unit} disponibili sul lotto scelto — scegli un altro lotto o riduci la quantità.`,
        });
      } else {
        toast.error("Errore", { description: detail || err.message });
      }
    } finally {
      setSaving(false);
    }
  };

  const priceDisplay = selected?.price_per_kg
    ? `${formatCurrency(selected.price_per_kg)}/kg`
    : formatCurrency(selected?.price || 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle style={{ fontFamily: "Work Sans, sans-serif" }}>
            Registra vendita
          </SheetTitle>
          <SheetDescription>
            Lo stock verrà aggiornato automaticamente.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={submit} className="space-y-4 mt-6">
          {product ? (
            <div className="p-4 rounded-lg bg-[#F4F1ED] border border-[#EAE7E1]">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#8E918C] mb-1">Prodotto</div>
              <div className="font-medium">{product.name}</div>
              <div className="text-xs text-[#5C5F5A] mt-1">
                Disponibili: {availableKg !== null && availableKg !== undefined ? formatKg(availableKg) : `${product.stock} pz`} · {priceDisplay}
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">Prodotto</Label>
              <Input
                data-testid="sale-product-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca per nome, PLU o codice fornitore"
                className="mb-2"
              />
              <Select value={productId} onValueChange={handleProductChange}>
                <SelectTrigger data-testid="sale-product-select">
                  <SelectValue placeholder={visibleProducts.length ? "Seleziona prodotto" : "Nessun risultato"} />
                </SelectTrigger>
                <SelectContent>
                  {visibleProducts.map((p) => {
                    const stockLabel = p.stock_kg !== null && p.stock_kg !== undefined
                      ? formatKg(p.stock_kg)
                      : `${p.stock} pz`;
                    const codes = [
                      p.plu ? `PLU ${p.plu}` : null,
                      p.supplier_code || null,
                    ].filter(Boolean).join(" · ");
                    return (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{codes ? ` (${codes})` : ""} — {stockLabel}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Mode toggle */}
          <div className="flex items-center gap-2 p-2 rounded-md bg-[#F4F1ED] border border-[#EAE7E1]">
            <Weight size={14} className="text-[#2E4A3B]" />
            <span className="text-xs font-medium flex-1">Vendita a:</span>
            <div className="flex gap-1">
              <button
                type="button"
                data-testid="sale-sheet-mode-weight"
                onClick={() => changeMode("weight")}
                className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${
                  mode === "weight"
                    ? "bg-[#2E4A3B] text-white"
                    : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
                }`}
              >
                Peso (kg)
              </button>
              <button
                type="button"
                data-testid="sale-sheet-mode-piece"
                onClick={() => changeMode("piece")}
                className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${
                  mode === "piece"
                    ? "bg-[#2E4A3B] text-white"
                    : "bg-white text-[#5C5F5A] border border-[#EAE7E1] hover:border-[#2E4A3B]"
                }`}
              >
                Pezzo
              </button>
            </div>
          </div>

          {/* ── Multi-Lotto: selettore lotto (solo se >1 lotto disponibile) ── */}
          {availableLots.length > 1 && !insufficientLots && (
            <div className="space-y-1.5" data-testid="sale-lot-picker">
              <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">
                Scarica dal lotto
              </Label>
              <div className="space-y-1.5">
                {availableLots.map((l, idx) => {
                  const isSelected = selectedLotId === l.id;
                  const isFefo = idx === 0;
                  const batchLabel = (l.batch || "").trim() || "Senza lotto";
                  const expLabel = l.expiry_date ? formatDate(l.expiry_date) : "Senza scadenza";
                  return (
                    <button
                      type="button"
                      key={l.id}
                      onClick={() => setSelectedLotId(l.id)}
                      data-testid={`sale-lot-option-${l.id}`}
                      className={`w-full flex flex-wrap items-center gap-2 p-2.5 rounded-md border text-left transition-all ${
                        isSelected
                          ? "bg-[#E8F3EC] border-[#2E4A3B]"
                          : "bg-white border-[#EAE7E1] hover:border-[#2E4A3B]"
                      }`}
                    >
                      <Package size={14} className={isSelected ? "text-[#2E4A3B]" : "text-[#8E918C]"} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono">{batchLabel}</span>
                          {isFefo && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FDF6E3] text-[#8B6518] border border-[#F2DEB0]">
                              FEFO
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#8E918C] mt-0.5">
                          Scad. {expLabel}
                        </div>
                      </div>
                      <div
                        data-testid={`sale-lot-option-avail-${l.id}`}
                        className={`text-xs font-mono font-semibold ${isSelected ? "text-[#2B5A3F]" : "text-[#5C5F5A]"}`}
                      >
                        {_formatLotAvailable(l, isWeight)}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-[#8E918C] px-1">
                Il primo lotto (scadenza più vicina) è preselezionato — cambialo con un tocco.
              </div>
            </div>
          )}

          {/* Banner errore lotto insufficiente: riapre la scelta con i dati aggiornati dal backend */}
          {insufficientLots && (
            <div
              data-testid="sale-lot-insufficient-banner"
              className="p-3 rounded-md bg-[#FBE9E7] border border-[#E88A6E] space-y-2"
            >
              <div className="flex items-start gap-1.5 text-xs text-[#8B2E1A]">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <div className="min-w-0 break-words">
                  <div className="font-semibold">Lotto insufficiente</div>
                  <div className="mt-0.5">
                    Sul lotto scelto ci sono {lotInsufficient.lot_available} {lotInsufficient.unit},
                    ne servono {lotInsufficient.requested}. Scegli un altro lotto o riduci la quantità.
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                {(insufficientLots || []).map((l) => {
                  const lotId = l.lot_id;
                  const isSelected = selectedLotId === lotId;
                  const label = (l.batch || "").trim() || "Senza lotto";
                  const exp = l.expiry_date ? formatDate(l.expiry_date) : "Senza scadenza";
                  const avail = isWeight
                    ? formatKg(l.available || 0)
                    : `${Number(l.available || 0)} pz`;
                  return (
                    <button
                      type="button"
                      key={lotId}
                      onClick={() => { setSelectedLotId(lotId); setLotInsufficient(null); }}
                      data-testid={`sale-lot-insufficient-option-${lotId}`}
                      className={`w-full flex flex-wrap items-center gap-2 p-2 rounded-md border text-left transition-all ${
                        isSelected
                          ? "bg-white border-[#2E4A3B]"
                          : "bg-white border-[#EAE7E1] hover:border-[#8B2E1A]"
                      }`}
                    >
                      <Package size={12} className="text-[#8B2E1A]" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium font-mono">{label}</div>
                        <div className="text-[11px] text-[#8E918C]">Scad. {exp}</div>
                      </div>
                      <div className="text-xs font-mono font-semibold text-[#8B2E1A]">{avail}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {mode === "weight" ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">Peso venduto</Label>
                <Input
                  data-testid="sale-weight-input"
                  placeholder="0.522 kg"
                  value={weightStr}
                  onChange={(e) => setWeightStr(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">€ al kg</Label>
                <Input
                  data-testid="sale-price-per-kg-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={pricePerKg}
                  onChange={(e) => setPricePerKg(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">Quantità</Label>
                <Input
                  data-testid="sale-qty-input"
                  type="number"
                  min="1"
                  max={selected?.stock || 9999}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#5C5F5A] uppercase tracking-wider">Prezzo unitario (€)</Label>
                <Input
                  data-testid="sale-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-4 rounded-lg bg-[#E8F3EC] border border-[#C5E1D1]">
            <span className="text-sm font-medium text-[#2B5A3F]">Totale</span>
            <span className="text-xl font-semibold text-[#2B5A3F]" style={{ fontFamily: "Work Sans, sans-serif" }}>
              {formatCurrency(total)}
            </span>
          </div>

          <SheetFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#EAE7E1]"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={saving || !productId}
              data-testid="sale-save-btn"
              className="bg-[#2E4A3B] hover:bg-[#21382C] text-white"
            >
              {saving ? "Salvataggio..." : "Registra vendita"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
